import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import config from '../config/config';
import { authFetch } from '../utils/api';
import Pagination, { DEFAULT_PAGE_SIZE } from './Pagination';
import Skeleton from './common/Skeleton';
const BASE_URL = config.BASE_URL;

async function fetchMatrixPage(params) {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value != null && value !== '') {
      query.set(key, String(value));
    }
  });
  const response = await authFetch(`${BASE_URL}/portfolio/matrix?${query.toString()}`);
  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }
  return response.json();
}

const GRADE_COLORS = { A: '#22c55e', B: '#84cc16', C: '#f59e0b', D: '#f97316', F: '#ef4444' };
const RISK_LABEL = (score) => {
  if (score >= 70) return { label: 'Critical', cls: 'badge-critical' };
  if (score >= 40) return { label: 'High', cls: 'badge-high' };
  if (score >= 20) return { label: 'Medium', cls: 'badge-medium' };
  return { label: 'Low', cls: 'badge-low' };
};

const LICENSE_RISK_CFG = {
  PERMISSIVE:      { label: 'Permissive',      color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0', icon: '🟢' },
  WEAK_COPYLEFT:   { label: 'Weak Copyleft',   color: '#d97706', bg: '#fffbeb', border: '#fde68a', icon: '🟡' },
  STRONG_COPYLEFT: { label: 'Strong Copyleft', color: '#dc2626', bg: '#fef2f2', border: '#fca5a5', icon: '🔴' },
  UNKNOWN:         { label: 'Unknown',          color: '#6b7280', bg: '#f9fafb', border: '#e5e7eb', icon: '⚪' },
};

function LicensePortfolio() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    authFetch(`${BASE_URL}/portfolio/licenses`)
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  if (loading) return (
    <div style={{ padding: '1rem 0' }}>
      <Skeleton.Table rows={4} cols={5} />
    </div>
  );
  if (!data) return null;

  const total = data.totalPackages || 1;
  const cats = [
    { key: 'permissiveCount',     label: t('compliance.permissive', 'Permissive'),      pct: Math.round(data.permissiveCount / total * 100), cfg: LICENSE_RISK_CFG.PERMISSIVE },
    { key: 'weakCopyleftCount',   label: t('compliance.weakCopyleft', 'Weak Copyleft'),   pct: Math.round(data.weakCopyleftCount / total * 100), cfg: LICENSE_RISK_CFG.WEAK_COPYLEFT },
    { key: 'strongCopyleftCount', label: t('compliance.strongCopyleft', 'Strong Copyleft'), pct: Math.round(data.strongCopyleftCount / total * 100), cfg: LICENSE_RISK_CFG.STRONG_COPYLEFT },
    { key: 'unknownCount',        label: t('compliance.unknown', 'Unknown'),          pct: Math.round(data.unknownCount / total * 100), cfg: LICENSE_RISK_CFG.UNKNOWN },
  ];

  return (
    <div className="mt-5">
      <h2 className="ce-page-title mb-1" style={{ fontSize: '1.25rem' }}>
        <span style={{ marginRight: '0.5rem' }}>⚖️</span>{t('compliance.portfolioLicenseDistribution', 'Portfolio License Distribution')}
      </h2>
      <p className="ce-page-subtitle mb-3">License risk across all {data.repos?.length || 0} repos — {total.toLocaleString()} packages total</p>

      {/* Global distribution bar */}
      <div style={{ height: 20, borderRadius: 8, overflow: 'hidden', display: 'flex', marginBottom: '0.75rem', background: '#f1f5f9' }}>
        {cats.filter(c => c.pct > 0).map(c => (
          <div key={c.key} style={{ width: `${c.pct}%`, background: c.cfg.color, transition: 'width .3s' }} title={`${c.label}: ${c.pct}%`} />
        ))}
      </div>
      <div className="d-flex flex-wrap gap-3 mb-4">
        {cats.map(c => (
          <div key={c.key} className="d-flex align-items-center gap-1" style={{ fontSize: '0.8125rem' }}>
            <span>{c.cfg.icon}</span>
            <span style={{ fontWeight: 600, color: c.cfg.color }}>{data[c.key]}</span>
            <span className="text-muted">{c.label} ({c.pct}%)</span>
          </div>
        ))}
      </div>

      {/* Per-repo table */}
      <div className="ce-table-wrap">
        <table className="ce-table" style={{ fontSize: '0.8125rem' }}>
          <thead>
            <tr>
              <th>{t('compliance.repository', 'Repository')}</th>
              <th>{t('compliance.packages', 'Packages')}</th>
              <th>{t('compliance.licenseDistribution', 'License Distribution')}</th>
              <th style={{ whiteSpace: 'nowrap' }}>🟢 / 🟡 / 🔴 / ⚪</th>
              <th>{t('compliance.topLicenses', 'Top Licenses')}</th>
            </tr>
          </thead>
          <tbody>
            {(data.repos || []).map(r => {
              return (
                <tr key={r.repoName}>
                  <td style={{ fontWeight: 600 }}>{r.repoName}</td>
                  <td>{r.totalPackages}</td>
                  <td style={{ minWidth: 140 }}>
                    <div style={{ height: 8, borderRadius: 4, overflow: 'hidden', display: 'flex', background: '#f1f5f9' }}>
                      {r.permissivePct > 0     && <div style={{ width: `${r.permissivePct}%`,     background: '#16a34a' }} title={`Permissive ${r.permissivePct}%`} />}
                      {r.weakCopyleftPct > 0   && <div style={{ width: `${r.weakCopyleftPct}%`,   background: '#d97706' }} title={`Weak Copyleft ${r.weakCopyleftPct}%`} />}
                      {r.strongCopyleftPct > 0 && <div style={{ width: `${r.strongCopyleftPct}%`, background: '#dc2626' }} title={`Strong Copyleft ${r.strongCopyleftPct}%`} />}
                      {r.unknownPct > 0        && <div style={{ width: `${r.unknownPct}%`,        background: '#9ca3af' }} title={`Unknown ${r.unknownPct}%`} />}
                    </div>
                  </td>
                  <td style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                    <span style={{ color: '#16a34a' }}>{r.permissiveCount}</span>
                    {' / '}
                    <span style={{ color: '#d97706' }}>{r.weakCopyleftCount}</span>
                    {' / '}
                    <span style={{ color: r.strongCopyleftCount > 0 ? '#dc2626' : 'inherit', fontWeight: r.strongCopyleftCount > 0 ? 700 : 400 }}>{r.strongCopyleftCount}</span>
                    {' / '}
                    <span style={{ color: r.unknownPct > 30 ? '#d97706' : '#6b7280' }}>{r.unknownCount}</span>
                  </td>
                  <td>
                    <div className="d-flex flex-wrap gap-1">
                      {(r.topLicenses || []).slice(0, 3).map(l => (
                        <span key={l} className="ce-tag">{l}</span>
                      ))}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="d-flex flex-wrap gap-3 mt-2" style={{ fontSize: '0.75rem', color: 'var(--bs-secondary-color)' }}>
        {cats.map(c => <span key={c.key}>{c.cfg.icon} {c.label}: {c.cfg.label === 'Permissive' ? t('compliance.descPermissive', 'MIT/Apache/BSD') : c.cfg.label === 'Weak Copyleft' ? t('compliance.descWeakCopyleft', 'LGPL/MPL/EPL') : c.cfg.label === 'Strong Copyleft' ? t('compliance.descStrongCopyleft', 'GPL/AGPL — commercial risk') : t('compliance.descUnknown', 'Not declared')}</span>)}
      </div>
    </div>
  );
}

export default function ComplianceMatrix() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totals, setTotals] = useState({
    repos: 0,
    vulns: 0,
    critical: 0,
    anomalies: 0,
    reachable: 0,
    notAffected: 0,
    investigating: 0,
    reachScanned: 0,
    identifierGaps: 0,
    passing: 0,
    ntia: 0,
    craReady: 0,
  });

  const sortKey     = searchParams.get('cm_sort')   || 'riskScore';
  const sortAsc     = searchParams.get('cm_asc')    === 'true';
  const filterRisk  = searchParams.get('cm_risk')   || 'all';
  const filterGrade = searchParams.get('cm_grade')  || 'all';
  const filterPolicy= searchParams.get('cm_policy') || 'all';
  const filterCRA   = searchParams.get('cm_cra')    || 'all';
  const search      = searchParams.get('cm_search') || '';
  const activeView  = searchParams.get('cm_view')   || 'matrix';
  const page        = Math.max(1, parseInt(searchParams.get('cm_page') || '1', 10));
  const [pageSize, setPageSizeState] = useState(() => parseInt(localStorage.getItem('depguard_page_size') || String(DEFAULT_PAGE_SIZE), 10));

  const setParam = (key, val) => setSearchParams(prev => { const n = new URLSearchParams(prev); n.set(key, val); return n; });

  const setFilterRisk   = (val) => setSearchParams(prev => { const n = new URLSearchParams(prev); n.set('cm_risk', val); n.set('cm_page', '1'); return n; });
  const setFilterGrade  = (val) => setSearchParams(prev => { const n = new URLSearchParams(prev); n.set('cm_grade', val); n.set('cm_page', '1'); return n; });
  const setFilterPolicy = (val) => setSearchParams(prev => { const n = new URLSearchParams(prev); n.set('cm_policy', val); n.set('cm_page', '1'); return n; });
  const setFilterCRA    = (val) => setSearchParams(prev => { const n = new URLSearchParams(prev); n.set('cm_cra', val); n.set('cm_page', '1'); return n; });
  const setSearch       = (val) => setSearchParams(prev => { const n = new URLSearchParams(prev); n.set('cm_search', val); n.set('cm_page', '1'); return n; });
  const setActiveView   = (val) => setParam('cm_view', val);
  const setPage         = (val) => setParam('cm_page', String(val));
  const setPageSize = (val) => {
    localStorage.setItem('depguard_page_size', String(val));
    setPageSizeState(val);
    setSearchParams(prev => {
      const n = new URLSearchParams(prev);
      n.set('cm_page', '1');
      return n;
    });
  };

  useEffect(() => {
    if (activeView !== 'matrix') {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);

    fetchMatrixPage({
      sort: sortKey,
      asc: sortAsc,
      risk: filterRisk,
      grade: filterGrade,
      policy: filterPolicy,
      cra: filterCRA,
      search,
      page,
      page_size: pageSize,
    })
      .then((data) => {
        if (cancelled) return;
        setRows(data?.rows || []);
        setTotals(data?.totals || {
          repos: 0,
          vulns: 0,
          critical: 0,
          anomalies: 0,
          reachable: 0,
          notAffected: 0,
          investigating: 0,
          reachScanned: 0,
          identifierGaps: 0,
          passing: 0,
          ntia: 0,
          craReady: 0,
        });
        setTotalItems(data?.totalItems || 0);
        setTotalPages(data?.totalPages || 0);
        setLoading(false);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e.message || 'HTTP 500');
        setLoading(false);
      });

    return () => { cancelled = true; };
  }, [activeView, sortKey, sortAsc, filterRisk, filterGrade, filterPolicy, filterCRA, search, page, pageSize]);

  const sort = useCallback((key) => {
    setSearchParams(prev => {
      const n = new URLSearchParams(prev);
      if (sortKey === key) {
        n.set('cm_asc', String(!(prev.get('cm_asc') === 'true')));
      } else {
        n.set('cm_sort', key);
        n.set('cm_asc', 'false');
      }
      n.set('cm_page', '1');
      return n;
    });
  }, [setSearchParams, sortKey]);

  const exportCsv = async () => {
    try {
      const exportRows = [];
      let exportPage = 1;
      let exportTotalPages = 1;
      do {
        const data = await fetchMatrixPage({
          sort: sortKey,
          asc: sortAsc,
          risk: filterRisk,
          grade: filterGrade,
          policy: filterPolicy,
          cra: filterCRA,
          search,
          page: exportPage,
          page_size: 500,
        });
        exportRows.push(...(data?.rows || []));
        exportTotalPages = data?.totalPages || 0;
        exportPage += 1;
      } while (exportPage <= exportTotalPages);

    const header = ['Repository', 'Risk Score', 'Risk Level', 'Total Vulnerabilities', 'Critical', 'High', 'Medium', 'Low', 'Reachability Scanned', 'Reachable', 'Not Affected', 'Under Investigation', 'Maintainer Anomalies', 'High Maintainer Anomalies', 'SBOM Quality Grade', 'SBOM Quality Score', 'CRA Grade', 'CRA Score', 'PURL Coverage', 'CPE Coverage', 'Missing Both Identifiers', 'NTIA Compliant', 'Policy', 'Policy Score'];
    const csvRows = exportRows.map(r => [
      r.repoName, r.riskScore.toFixed(1), RISK_LABEL(r.riskScore).label,
      r.totalVulns, r.criticalVulns, r.highVulns, r.mediumVulns, r.lowVulns,
      r.reachabilityScanned ? 'Yes' : 'No', r.reachableVulns || 0, r.notAffectedVulns || 0, r.underInvestigationVulns || 0,
      r.anomalyCount || 0, r.highAnomalies || 0,
      r.qualityGrade || '-', r.qualityScore || '-', r.craGrade || '-', r.craScore || '-',
      r.identifierTotalPackages ? `${r.packagesWithPurl || 0}/${r.identifierTotalPackages}` : '-',
      r.identifierTotalPackages ? `${r.packagesWithCpe || 0}/${r.identifierTotalPackages}` : '-',
      r.packagesMissingBothIds ?? '-',
      r.ntiaCompliant ? 'Yes' : 'No',
      r.policyPassed == null ? 'N/A' : r.policyPassed ? 'Pass' : 'Fail',
      r.policyScore ?? '-',
    ]);
    const csv = [header, ...csvRows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'compliance-matrix.csv'; a.click();
    URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.message || 'HTTP 500');
    }
  };

  const SortIcon = ({ k }) => {
    if (sortKey !== k) return <span style={{ opacity: 0.3, fontSize: '0.7rem' }}>⇅</span>;
    return <span style={{ fontSize: '0.75rem' }}>{sortAsc ? '↑' : '↓'}</span>;
  };

  const Th = ({ k, children }) => (
    <th
      onClick={() => sort(k)}
      style={{ cursor: 'pointer', whiteSpace: 'nowrap', userSelect: 'none' }}
    >
      {children} <SortIcon k={k} />
    </th>
  );

  return (
    <div className="App-body">
      {/* Header */}
      <div className="d-flex align-items-center gap-3 mb-3 flex-wrap">
        <div>
          <h1 className="ce-page-title mb-1">
            <span style={{ marginRight: '0.5rem' }}>📋</span>
            {t('matrix.title', 'Compliance Matrix')}
          </h1>
          <p className="ce-page-subtitle mb-0">{t('matrix.subtitle', 'Portfolio overview — all repositories at a glance')}</p>
        </div>
        <div className="ms-auto d-flex gap-2">
          {activeView === 'matrix' && (
            <button className="ce-btn ce-btn--secondary ce-btn--sm" onClick={exportCsv} title="Export als CSV">
              ⬇️ CSV
            </button>
          )}
        </div>
      </div>

      {/* View toggle tabs */}
      <div className="ce-tabs mb-4">
        <button className={`ce-tab-btn${activeView === 'matrix' ? ' active' : ''}`} onClick={() => setActiveView('matrix')}>
          {t('compliance.riskMatrix', '📋 Risk Matrix')}
        </button>
        <button className={`ce-tab-btn${activeView === 'licenses' ? ' active' : ''}`} onClick={() => setActiveView('licenses')}>
          {t('compliance.licenseDistributionTab', '⚖️ License Distribution')}
        </button>
      </div>

      {/* License view */}
      {activeView === 'licenses' && <LicensePortfolio />}

      {/* Matrix view */}
      {activeView === 'matrix' && (<>

      {!loading && !error && (
        <div className="ce-kpi-row mb-4">
          {[
            { icon: '🗂️', label: t('matrix.repos', 'Repositories'), value: totals.repos },
            { icon: '🔓', label: t('matrix.totalVulns', 'Kwetsbaarheden'), value: totals.vulns, highlight: totals.vulns > 0 },
            { icon: '🔴', label: t('matrix.critical', 'Kritiek'), value: totals.critical, highlight: totals.critical > 0, danger: true },
            { icon: '🕵️', label: t('matrix.anomalies', 'Maintainer Anomalies'), value: totals.anomalies, highlight: totals.anomalies > 0 },
            { icon: '🧭', label: t('matrix.reachable', 'Reachable CVEs'), value: totals.reachable ?? 0, highlight: (totals.reachable ?? 0) > 0, danger: (totals.reachable ?? 0) > 0 },
            { icon: '🛣️', label: t('matrix.reachScanned', 'Reachability scanned'), value: `${totals.reachScanned ?? 0}/${totals.repos}` },
            { icon: '🪪', label: t('matrix.identifierGaps', 'Identifier gaps'), value: totals.identifierGaps, highlight: totals.identifierGaps > 0 },
            { icon: '✅', label: t('matrix.policyPass', 'Policy pass'), value: `${totals.passing}/${totals.repos}` },
            { icon: '🏛️', label: t('matrix.ntia', 'NTIA compliant'), value: `${totals.ntia}/${totals.repos}` },
            { icon: '🇪🇺', label: t('matrix.craReady', 'CRA ready'), value: `${totals.craReady}/${totals.repos}` },
          ].map(s => (
            <div key={s.label} className="ce-kpi" style={s.danger && s.value > 0 ? { '--kpi-color': '#dc2626' } : {}}>
              <div className="ce-kpi-value">{s.icon} {s.value}</div>
              <div className="ce-kpi-label">{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="ce-filter-bar mb-3">
        <input
          className="ce-search-input"
          placeholder={t('matrix.search', 'Search repository...')}
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select className="ce-filter-select" value={filterRisk} onChange={e => setFilterRisk(e.target.value)}>
           <option value="all">{t('matrix.allRisks', 'All risks')}</option>
          <option value="critical">🔴 Critical</option>
          <option value="high">🟠 High</option>
          <option value="medium">🟡 Medium</option>
          <option value="low">🟢 Low</option>
        </select>
        <select className="ce-filter-select" value={filterGrade} onChange={e => setFilterGrade(e.target.value)}>
           <option value="all">{t('matrix.allGrades', 'All grades')}</option>
          {['A', 'B', 'C', 'D', 'F'].map(g => <option key={g} value={g}>Grade {g}</option>)}
          <option value="?">Unknown</option>
        </select>
        <select className="ce-filter-select" value={filterPolicy} onChange={e => setFilterPolicy(e.target.value)}>
           <option value="all">{t('matrix.allPolicy', 'All policy states')}</option>
          <option value="pass">✅ Pass</option>
          <option value="fail">❌ Fail</option>
           <option value="none">— Not scanned</option>
        </select>
        <select className="ce-filter-select" value={filterCRA} onChange={e => setFilterCRA(e.target.value)}>
          <option value="all">{t('matrix.allCra', 'All CRA states')}</option>
          <option value="ready">{t('matrix.craReady', 'CRA ready')}</option>
          <option value="at_risk">{t('matrix.craAtRisk', 'CRA at risk')}</option>
          <option value="missing">{t('matrix.craMissing', 'CRA missing')}</option>
        </select>
        {(search || filterRisk !== 'all' || filterGrade !== 'all' || filterPolicy !== 'all' || filterCRA !== 'all') && (
          <button className="ce-btn ce-btn--sm" onClick={() => { setSearch(''); setFilterRisk('all'); setFilterGrade('all'); setFilterPolicy('all'); setFilterCRA('all'); setPage('1'); }}>
            ✕ Reset
          </button>
        )}
        <span className="ce-filter-count">{totalItems} {totalItems !== 1 ? 'repositories' : 'repository'}</span>
      </div>

      {/* Table */}
      {loading && (
        <div style={{ padding: '1rem 0' }}>
          <Skeleton.Table rows={6} cols={8} />
        </div>
      )}
      {error && <div className="alert alert-danger">{t('matrix.error', 'Matrix laden mislukt')}: {error}</div>}

      {!loading && !error && totalItems === 0 && (
        <div className="text-center py-5 text-muted">
          <div style={{ fontSize: '3rem' }}>📋</div>
          <p>{t('matrix.noData', 'No repositories found. Upload an SBOM to get started.')}</p>
        </div>
      )}

      {!loading && !error && totalItems > 0 && (
        <div className="ce-table-wrap">
          <table className="ce-table ce-table--clickable" style={{ minWidth: 1140 }}>
            <thead>
              <tr>
                <Th k="repoName">{t('compliance.repository', 'Repository')}</Th>
                <Th k="riskScore">{t('compliance.risk', 'Risk')}</Th>
                  <Th k="totalVulns">{t('compliance.vulnerabilities', 'Vulnerabilities')}</Th>
                <th style={{ whiteSpace: 'nowrap' }}>C / H / M / L</th>
                  <Th k="reachableVulns">{t('compliance.exploitability', 'Exploitability')}</Th>
                  <Th k="anomalyCount">{t('compliance.maintainerAnomalies', 'Maintainer Anomalies')}</Th>
                  <Th k="qualityScore">{t('compliance.sbomQuality', 'SBOM Quality')}</Th>
                  <Th k="craScore">CRA</Th>
                <th style={{ whiteSpace: 'nowrap' }}>IDs</th>
                <th>NTIA</th>
                <Th k="policyScore">Policy</Th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map(row => {
                const risk = RISK_LABEL(row.riskScore);
                const grade = row.qualityGrade || '?';
                const gradeColor = GRADE_COLORS[grade] || '#94a3b8';
                return (
                  <tr key={row.repoName} style={{ cursor: 'pointer' }} onClick={() => navigate(`/RepoDetails/${encodeURIComponent(row.repoName)}`)}>
                    {/* Repo name */}
                    <td>
                      <div style={{ fontWeight: 600, color: 'var(--bs-body-color)' }}>{row.repoName}</div>
                      {row.lastScanned && (
                        <div style={{ fontSize: '0.7rem', color: 'var(--bs-secondary-color)' }}>
                          {new Date(row.lastScanned).toLocaleDateString()}
                        </div>
                      )}
                    </td>

                    {/* Risk score */}
                    <td>
                      <div className="d-flex align-items-center gap-2">
                        <div className="ce-score-bar" style={{ width: 60 }}>
                          <div className={`ce-score-bar-fill ce-score-bar-fill--${risk.label === 'Critical' || risk.label === 'High' ? 'red' : risk.label === 'Medium' ? 'yellow' : 'green'}`} style={{ width: `${row.riskScore}%` }} />
                        </div>
                        <span className={`ce-sev ce-sev--${risk.label.toLowerCase()}`}>
                          {risk.label} {row.riskScore.toFixed(0)}
                        </span>
                      </div>
                    </td>

                    {/* Total vulns */}
                    <td>
                      <span style={{ fontWeight: row.totalVulns > 0 ? 700 : 400, color: row.totalVulns > 0 ? '#dc2626' : 'var(--bs-secondary-color)' }}>
                        {row.totalVulns}
                      </span>
                    </td>

                    {/* Severity breakdown */}
                    <td>
                      <span style={{ color: '#dc2626', fontWeight: 600 }}>{row.criticalVulns}</span>
                      {' / '}
                      <span style={{ color: '#ea580c' }}>{row.highVulns}</span>
                      {' / '}
                      <span style={{ color: '#d97706' }}>{row.mediumVulns}</span>
                      {' / '}
                      <span style={{ color: '#6b7280' }}>{row.lowVulns}</span>
                    </td>

                      <td>
                        {row.reachabilityScanned ? (
                          <div className="d-flex align-items-center gap-1 flex-wrap">
                            <span className={`ce-sev ${(row.reachableVulns || 0) > 0 ? 'ce-sev--critical' : 'ce-sev--low'}`}>
                              {(row.reachableVulns || 0) > 0 ? `${row.reachableVulns} reachable` : `${row.notAffectedVulns || 0} not affected`}
                            </span>
                            {(row.underInvestigationVulns || 0) > 0 && (
                              <span style={{ fontSize: '0.72rem', color: '#d97706', fontWeight: 600 }}>
                                {row.underInvestigationVulns} investigating
                              </span>
                            )}
                          </div>
                        ) : <span style={{ color: 'var(--bs-secondary-color)' }}>—</span>}
                      </td>

                      {/* Anomalies */}
                      <td>
                        {(row.anomalyCount || 0) > 0 ? (
                          <div className="d-flex align-items-center gap-1 flex-wrap">
                           <span className={`ce-sev ${(row.highAnomalies || 0) > 0 ? 'ce-sev--critical' : 'ce-sev--high'}`}>
                             {row.anomalyCount} total
                           </span>
                           {(row.highAnomalies || 0) > 0 && (
                             <span style={{ fontSize: '0.72rem', color: '#dc2626', fontWeight: 600 }}>
                               {row.highAnomalies} high
                             </span>
                           )}
                         </div>
                       ) : <span style={{ color: 'var(--bs-secondary-color)' }}>—</span>}
                     </td>

                     {/* SBOM grade */}
                     <td>
                       <div className="d-flex align-items-center gap-2">
                        {grade === '?' ? (
                          <span style={{ color: 'var(--bs-secondary-color)', fontSize: '0.85rem' }}>?</span>
                        ) : (
                          <span className={`ce-grade ce-grade--${grade.toLowerCase()}`}>{grade}</span>
                        )}
                        {grade !== '?' && (
                          <div className="ce-score-bar" style={{ width: 50 }}>
                            <div className={`ce-score-bar-fill ce-score-bar-fill--${grade === 'A' || grade === 'B' ? 'green' : grade === 'C' ? 'yellow' : 'red'}`} style={{ width: `${row.qualityScore}%` }} />
                          </div>
                        )}
                      </div>
                     </td>

                    {/* CRA grade */}
                    <td>
                      {row.craGrade ? (
                        <div className="d-flex align-items-center gap-2">
                          <span className={`ce-grade ce-grade--${row.craGrade.toLowerCase()}`}>
                            {row.craGrade}
                          </span>
                          <span style={{ fontSize: '0.75rem', color: 'var(--bs-secondary-color)' }}>
                            {row.craScore}/100
                          </span>
                        </div>
                      ) : <span style={{ color: 'var(--bs-secondary-color)' }}>—</span>}
                    </td>

                    <td>
                      {row.identifierTotalPackages ? (
                        <div style={{ fontSize: '0.72rem', lineHeight: 1.35 }}>
                          <div>PURL {row.packagesWithPurl || 0}/{row.identifierTotalPackages}</div>
                          <div>CPE {row.packagesWithCpe || 0}/{row.identifierTotalPackages}</div>
                          {(row.packagesMissingBothIds || 0) > 0 && (
                            <span className="badge bg-warning text-dark mt-1">
                              {row.packagesMissingBothIds} gap{row.packagesMissingBothIds > 1 ? 's' : ''}
                            </span>
                          )}
                        </div>
                      ) : <span style={{ color: 'var(--bs-secondary-color)' }}>—</span>}
                    </td>

                    {/* NTIA */}
                    <td>
                      {row.qualityGrade ? (
                        <span title={row.ntiaCompliant ? 'NTIA Minimum Elements compliant' : 'Niet NTIA compliant'}>
                          {row.ntiaCompliant ? '✅' : '❌'}
                        </span>
                      ) : <span style={{ color: 'var(--bs-secondary-color)' }}>—</span>}
                    </td>

                    {/* Policy */}
                    <td>
                      {row.policyPassed == null ? (
                        <span style={{ color: 'var(--bs-secondary-color)', fontSize: '0.8rem' }}>—</span>
                      ) : (
                        <div className="d-flex align-items-center gap-1">
                          <span>{row.policyPassed ? '✅' : '❌'}</span>
                          <span style={{ fontSize: '0.75rem', color: 'var(--bs-secondary-color)' }}>{row.policyScore}%</span>
                        </div>
                      )}
                    </td>

                    {/* Actions */}
                    <td onClick={e => e.stopPropagation()}>
                      <button
                        className="ce-btn ce-btn--secondary ce-btn--sm"
                        onClick={() => navigate(`/RepoDetails/${encodeURIComponent(row.repoName)}`)}
                      >
                        {t('compliance.details', 'Details →')}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {!loading && !error && totalPages > 1 && (
        <div className="mt-3">
          <Pagination
            nPages={totalPages}
            currentPage={page}
            setCurrentPage={setPage}
            pageSize={pageSize}
            setPageSize={setPageSize}
            totalItems={totalItems}
          />
        </div>
      )}

      {/* Legend */}
      {!loading && !error && (
        <div className="d-flex flex-wrap gap-3 mt-3" style={{ fontSize: '0.75rem', color: 'var(--bs-secondary-color)' }}>
          <span>🔴 {t('compliance.legendCritical', 'Critical ≥70')}</span>
          <span>🟠 {t('compliance.legendHigh', 'High ≥40')}</span>
          <span>🟡 {t('compliance.legendMedium', 'Medium ≥20')}</span>
          <span>🟢 {t('compliance.legendLow', 'Low <20')}</span>
          <span style={{ marginLeft: 'auto' }}>Risicoscore = CVEs × gewicht + exploitability + SBOM kwaliteit + policy status + maintainer anomalies</span>
        </div>
      )}
      </>)}
    </div>
  );
}
